// Frontend validation for AC5 rules (client-side for instant UX feedback).
// Backend counterpart: Consultant.Appraisals.Service/Services/AppraisalValidationService.cs
// Both must be kept in sync when validation rules change.
import type {
  AppraisalDetailResponse,
  InterviewSummaryEntry,
} from '@/api/types/appraisal.types';

export interface AppraisalValidationErrors {
  selfAssessment: boolean;
  skillAreaStrengths: boolean;
  forwardLookingPlan: boolean;
  proposedRating: boolean;
  performanceSummary: boolean;
  interviewCandidates: boolean;
}

export const NO_ERRORS: AppraisalValidationErrors = {
  selfAssessment: false,
  skillAreaStrengths: false,
  forwardLookingPlan: false,
  proposedRating: false,
  performanceSummary: false,
  interviewCandidates: false,
};

const stripHtml = (html: string): string => html.replace(/<[^>]*>/g, '').trim();

const isEmpty = (value: string | null | undefined): boolean =>
  value == null || stripHtml(value).length === 0;

export function validateForHandover(
  appraisal: AppraisalDetailResponse
): AppraisalValidationErrors {
  const items = appraisal.selfAssessmentItems ?? [];
  const selfAssessment = items.some((item) => isEmpty(item.appraiseeResponse));

  return { ...NO_ERRORS, selfAssessment };
}

export function validateForSubmitToTc(
  appraisal: AppraisalDetailResponse,
  interviewSummaryEntries: InterviewSummaryEntry[]
): AppraisalValidationErrors {
  const feedback = appraisal.skillAreaFeedback ?? [];
  const skillAreaStrengths = feedback.some(
    (f) => f.rating == null || isEmpty(f.combinedText)
  );

  const plan = appraisal.forwardLookingPlan ?? [];
  const needsWithText = plan.filter((item) => !isEmpty(item.developmentNeed));
  const actionsWithText = plan.filter((item) => !isEmpty(item.actionToResolve));
  const forwardLookingPlan = needsWithText.length < 3 || actionsWithText.length < 3;

  const proposedRating = appraisal.proposedRating == null;

  const performanceSummary = isEmpty(appraisal.performanceSummary);

  const interviewCandidates =
    !interviewSummaryEntries.some((e) => e.planned) ||
    !interviewSummaryEntries.some((e) => e.completed);

  return {
    selfAssessment: false,
    skillAreaStrengths,
    forwardLookingPlan,
    proposedRating,
    performanceSummary,
    interviewCandidates,
  };
}

export function hasErrors(errors: AppraisalValidationErrors): boolean {
  return Object.values(errors).some(Boolean);
}

const HANDOVER_MESSAGES: Record<string, string> = {
  selfAssessment:
    'Self-Assessment \u2014 all Appraisee Responses must be completed before handing over to the Collator',
};

const SUBMIT_TO_TC_MESSAGES: Record<string, string> = {
  skillAreaStrengths:
    'Strengths & Development Needs by Skill Area \u2014 all ratings and descriptions must be completed',
  forwardLookingPlan:
    'Forward-Looking Plan \u2014 at least 3 development needs and 3 actions must be completed',
  proposedRating: 'Proposed Rating \u2014 must be completed',
  performanceSummary: 'Performance Summary \u2014 must be filled out',
  interviewCandidates:
    'Interview Candidates \u2014 at least 1 interview must be planned and at least 1 completed',
};

export function getErrorMessages(
  errors: AppraisalValidationErrors,
  type: 'handover' | 'submitToTc'
): string[] {
  const messageMap = type === 'handover' ? HANDOVER_MESSAGES : SUBMIT_TO_TC_MESSAGES;
  return Object.entries(messageMap)
    .filter(([key]) => errors[key as keyof AppraisalValidationErrors])
    .map(([, message]) => message);
}
